import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import {
  OptionSelect,
  SearchSelect,
} from '../../../../shared/components/search-select/search-select';
import { Assistance, AssistanceDTO } from '../../../../core/models/assistance.interface';
import { Sector } from '../../../../core/models/sector.interface';
import { MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { SectorService } from '../../../../core/services/sector/sector';
import { LoadingService } from '../../../../core/services/loading/loading';
import { ServiceAssistance } from '../../../../core/services/assistance/assistance-service';
import { FormUtils } from '../../../../shared/utils';
import { switchMap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { Sourcefounding } from '../../../../core/models/sourcefounding.interface';
import { SourcefoundingService } from '../../../../core/services/sourcefounding/sourcefounding-service';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { SearchfilterPipe } from '../../../../shared/pipes/searchfilter.pipe';
import { TermPipe } from '../../../../shared/pipes/term.pipe';
import { TermKey } from '../../../../core/models/terminology.interface';

@Component({
  selector: 'app-assistance-form',
  imports: [
    MatFormField,
    ReactiveFormsModule,
    MatLabel,
    MatInput,
    MatError,
    MatIcon,
    MatIconModule,
    FormsModule,
    SearchSelect,
    MatOptionModule,
    MatSelectModule,
    SearchfilterPipe,
    TermPipe,
  ],
  templateUrl: './assistance-form.html',
  styleUrl: './assistance-form.scss',
})
export class AssistanceForm {
  assistance: string | null = null;
  errorMsg: string[] | null = null;
  assistanceSelected: Assistance | null = null;
  termKey = TermKey;

  sectorSelected: OptionSelect[] = [];
  listSector: Sector[] = [];
  selectSector: OptionSelect | null = null;
  sectorSeleccionado: Sector | null = null;

  soundingsourceSelected: OptionSelect[] = [];
  listSoundingsource: Sourcefounding[] = [];
  selectSoundingsource: OptionSelect | null = null;
  soundingsourceSeleccionado: Sourcefounding | null = null;
  private formBuilder = inject(FormBuilder);
  searchControl = new FormControl();

  constructor(
    private dialogRef: MatDialogRef<AssistanceForm>,
    private toastr: ToastrService,
    private serviceSector: SectorService,
    private loadingService: LoadingService,
    private serviceAssistance: ServiceAssistance,
    private sourcefoundingService: SourcefoundingService,
  ) {}

  ngOnInit(): void {
    this.fetchData();
    this.serviceAssistance.asyncErrorValidation$.subscribe((error) => {
      if (error) {
        this.errorMsg = FormUtils.parseErrorMsgs(error);
      }
    });
  }

  private fetchData() {
    this.serviceSector
      .getSectors()
      .pipe(
        switchMap((sectors) => {
          this.listSector = sectors;
          this.sectorSelected = this.serviceSector.getOptionSector(sectors);
          return this.sourcefoundingService.getFoundingsource();
        }),
        switchMap((sourceFoundings) => {
          this.listSoundingsource = sourceFoundings;
          this.soundingsourceSelected =
            this.sourcefoundingService.getOptionFoundingsources(sourceFoundings);
          return this.serviceAssistance.selectedAssistance$;
        }),
      )
      .subscribe((assistance) => {
        if (assistance) {
          this.assistance = String(assistance.id);
          this.assistanceSelected = assistance;
          this.assistanceForm.patchValue({
            ...this.assistanceSelected,
            sector: assistance.sector.map((sector) => sector.id),
            sourceFoundings: this.assistanceSelected.sourceFoundings?.id || 0,
          });
          const sourceFoundingsOpt = this.soundingsourceSelected.find((el) => {
            if (this.assistanceSelected) {
              return el.value === this.assistanceSelected.sourceFoundings?.id;
            }
            return false;
          });
          if (typeof sourceFoundingsOpt !== 'undefined') {
            this.selectSoundingsource = sourceFoundingsOpt;
          }
        }
      });
  }

  assistanceForm = this.formBuilder.group({
    name: ['', [Validators.required, Validators.maxLength(256)]],
    population: [0, [Validators.required]],
    description: ['', []],
    sector: this.formBuilder.control<number[]>([], Validators.required),
    sourceFoundings: [0, [Validators.required]],
  });

  getSourceFoundings(id: number) {
    const index = this.listSoundingsource.findIndex((soundingsource) => soundingsource.id === id);
    this.soundingsourceSeleccionado = this.listSoundingsource[index];
  }

  getSourceFoundingsSelected(sourceFoundings: OptionSelect) {
    this.assistanceForm.get('sourceFoundings')?.setValue(sourceFoundings.value as number);
    this.getSourceFoundings(sourceFoundings.value as number);
  }

  closeDialog() {
    this.dialogRef.close();
  }

  saveAssistance() {
    const payload = this.assistanceForm.value as AssistanceDTO;
    if (this.assistanceForm.status === 'VALID' && !this.assistance) {
      this.loadingService.show(true);
      this.serviceAssistance.createAssistance(payload).subscribe({
        next: (assistance) => {
          this.loadingService.show(false);
          const msg = `La asistencia ${assistance.name} fue CREADA correctamente!`;
          this.toastr.success(msg, 'Exitoso!', {
            progressBar: true,
            timeOut: 5000,
          });
          this.dialogRef.close();
        },
        error: ({}: HttpErrorResponse) => {
          if (this.errorMsg) {
            this.loadingService.show(false);
            this.toastr.error('Error: ' + this.errorMsg, undefined, {
              progressBar: true,
              timeOut: 5000,
            });
          }
        },
      });
    } else if (this.assistanceForm.status === 'VALID' && this.assistance) {
      this.loadingService.show(true);
      const id = Number(this.assistance);
      this.serviceAssistance.updateAssistance(id, { ...payload, id: id }).subscribe({
        next: (assistance) => {
          this.loadingService.show(false);
          const msg = `La asistencia ${assistance.name} fue EDITADA correctamente!`;
          this.toastr.success(msg, 'Exitoso!', {
            progressBar: true,
            timeOut: 5000,
          });
          this.dialogRef.close();
        },
        error: ({}: HttpErrorResponse) => {
          if (this.errorMsg) {
            this.loadingService.show(false);
            this.toastr.error('Error: ' + this.errorMsg, undefined, {
              progressBar: true,
              timeOut: 5000,
            });
          }
        },
      });
    }
  }
  getSectorValue() {
    return this.selectSector;
  }

  getSourceFoundingsValue() {
    return this.selectSoundingsource;
  }
}
